import React, { useState, useRef, useCallback } from "react";
import ReactDOM from "react-dom";

interface TooltipProps {
  text: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  placement?: "top" | "bottom" | "bottom-end";
  maxWidth?: number;
  /** data-testid applied to the tooltip bubble — lets automation tools locate it */
  testId?: string;
}

export function Tooltip({
  text,
  children,
  className,
  style,
  placement = "bottom",
  maxWidth,
  testId,
}: TooltipProps) {
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const ref = useRef<HTMLSpanElement>(null);
  const tooltipId = React.useId();

  const show = useCallback(() => {
    if (ref.current) {
      const r = ref.current.getBoundingClientRect();
      setPos({
        top: placement === "top" ? r.top - 6 : r.bottom + 6,
        left: placement === "bottom-end" ? r.right : r.left + r.width / 2,
      });
    }
  }, [placement]);

  const hide = useCallback(() => setPos(null), []);

  return (
    <span
      ref={ref}
      className={className}
      style={{ display: "inline-flex", alignItems: "center", ...style }}
      onMouseEnter={show}
      onMouseLeave={hide}
      aria-describedby={pos && text ? tooltipId : undefined}
    >
      {children}
      {pos && text &&
        ReactDOM.createPortal(
          <div
            id={tooltipId}
            role="tooltip"
            data-testid={testId ?? "tooltip"}
            style={{
              position: "fixed",
              top: pos.top,
              left: pos.left,
              transform:
                placement === "top"
                  ? "translateX(-50%) translateY(-100%)"
                  : placement === "bottom-end"
                  ? "translateX(-100%)"
                  : "translateX(-50%)",
              background: "rgba(0,0,0,0.82)",
              color: "#fff",
              padding: "4px 10px",
              borderRadius: "5px",
              fontSize: "11px",
              whiteSpace: maxWidth ? "normal" : "nowrap",
              wordBreak: maxWidth ? "break-word" : undefined,
              maxWidth: maxWidth ? `${maxWidth}px` : undefined,
              zIndex: 9999,
              pointerEvents: "none",
              lineHeight: "1.5",
            }}
          >
            {text}
          </div>,
          document.body
        )}
    </span>
  );
}
